import {
  DealItemWithVersions,
  PickupPointNetwork,
} from '../graphql/generated/graphql';

export enum ShippingDurationValue {
  '24h' = 1,
  '48h' = 2,
  '72h' = 3,
}

export type ShippingOfferDuration = '24h' | '48h' | '72h';

export interface ShippingPolicy {
  // Champs pour l'API
  freeShippingMinimumOrderAmount: number | null;
  deliveryOfferedNumberOfDays: number | null;
  pickupPointNetwork: PickupPointNetwork | null;
  basicFrPickupPointDeliveryPrice: number | null;

  // Champs pour l'UI (calculés à partir des champs de l'API)
  minimumOrderEnabled: boolean;
  minimumOrderAmount: number | null;
  timeLimitedEnabled: boolean;
  offerDuration: ShippingOfferDuration | null;
  pickupPointEnabled: boolean;
  pickupPointDeliveryPrice: number | null;
  allShippingFeesZero: boolean;
}

export interface ShippingPolicyProps {
  policy: ShippingPolicy;
  onPolicyChange: (policy: ShippingPolicy) => void;
  isLocked?: boolean;
  dealItems?: DealItemWithVersions[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hubspotStartDate?: Date | null;
}

// Fonction utilitaire pour convertir les jours en durée d'offre
export function daysToOfferDuration(
  days: number | null
): ShippingOfferDuration | null {
  if (days === null) return null;
  switch (days) {
    case 1:
      return '24h';
    case 2:
      return '48h';
    case 3:
      return '72h';
    default:
      return null;
  }
}

// Fonction utilitaire pour convertir la durée d'offre en jours
export function offerDurationToDays(
  duration: ShippingOfferDuration | null
): number | null {
  if (duration === null) return null;
  switch (duration) {
    case '24h':
      return 1;
    case '48h':
      return 2;
    case '72h':
      return 3;
    default:
      return null;
  }
}

/**
 * Calcule la date de début pour le montant minimum d'achat.
 * Si la durée limitée est activée, la date de début = hubspotStartDate + durée en jours.
 * Sinon, la date de début = hubspotStartDate.
 *
 * @param hubspotStartDate - Date de début de l'offre
 * @param timeLimitedEnabled - Indique si la durée limitée est activée
 * @param offerDuration - Durée de l'offre limitée (24h, 48h, 72h)
 * @returns Date calculée
 */
export function calculateMinimumOrderStartDate(
  hubspotStartDate: Date | null | undefined,
  timeLimitedEnabled: boolean,
  offerDuration: ShippingOfferDuration | null
): Date {
  const baseStartDate = hubspotStartDate
    ? new Date(hubspotStartDate)
    : new Date();

  const calculatedStartDate = new Date(baseStartDate);

  // Si la durée limitée est activée, ajouter les jours de durée à la date de début
  if (timeLimitedEnabled && offerDuration) {
    const days = offerDurationToDays(offerDuration);
    if (days) {
      calculatedStartDate.setDate(calculatedStartDate.getDate() + days);
    }
  }

  return calculatedStartDate;
}

/**
 * Calcule et formate la date de début pour le montant minimum d'achat.
 *
 * @param hubspotStartDate - Date de début de l'offre
 * @param timeLimitedEnabled - Indique si la durée limitée est activée
 * @param offerDuration - Durée de l'offre limitée (24h, 48h, 72h)
 * @param language - Code de langue pour le formatage (ex: 'fr', 'en')
 * @returns String formatée de la date
 */
export function formatMinimumOrderStartDate(
  hubspotStartDate: Date | null | undefined,
  timeLimitedEnabled: boolean,
  offerDuration: ShippingOfferDuration | null,
  language: string
): string {
  const calculatedDate = calculateMinimumOrderStartDate(
    hubspotStartDate,
    timeLimitedEnabled,
    offerDuration
  );

  return calculatedDate.toLocaleDateString(language, {
    day: 'numeric',
    month: 'short',
  });
}
